import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import getMatchingRecords from '../../utils/getMatchingRecords'
import { STADIUM_ORDER_STATES } from '../../utils/orderStates'
import { getActiveOfflineCardCount, getAllOrders, getOfflineCardIds, getOrderInProgress } from '../../selectors/order'
import { getIsPaxDevice } from '../../selectors/peripheral'

/**
 * Custom hook to manage order counters related to offline and syncable orders
 *
 * @returns {Object} - An object containing various order-related functions and data
 */
const useOrderCounters = () => {
  const orders = useSelector(getAllOrders)
  const orderIds = useSelector(getOfflineCardIds)
  const isPaxDevice = useSelector(getIsPaxDevice)
  const activeOfflineCardCount = useSelector(getActiveOfflineCardCount)
  const orderInProgress = useSelector(getOrderInProgress)

  const dateForOrder = useCallback((order) => {
    return moment(
      order?.syncedAt || order?.updatedAt || order?.modifiedAt || order?.submittedAt || order?.createdAt || order?.timestamp,
    )
  }, [])

  const getSyncableOfflineLocalIds = useCallback(
    (limit = undefined, byPassActiveOrder = false) => {
      const currentMoment = moment()

      // TODO: T41812 - this will be replaced with a Set instead of looking at order flags
      const matcher = (order) => {
        const curOrderId = order?.uuid || order?.id
        if (byPassActiveOrder && curOrderId === orderInProgress?.uuid) {
          return false
        }

        const orderDate = dateForOrder(order)
        let withinLastDay = false
        if (orderDate) {
          const hoursDifference = Math.abs(currentMoment.diff(orderDate, 'hours'))
          withinLastDay = hoursDifference <= 24
        }

        // Attempt the platform sync if:
        //   * the order is not unsyncable (to prevent re-syncing orders that are already synced)
        //   * the order was not created in stadium and is not syncing
        //   * the order is within the last 24 hours
        //   * the order is not an auth failed order (to prevent re-syncing auth failed orders)
        //   * conditionally set to ignore tab orders
        return (
          !order?.unsyncable &&
          ((!order?.wasCreatedInStadium && !order?.isSyncing) || order?.needsSync) &&
          order?.state !== STADIUM_ORDER_STATES.AUTHORIZATION_FAILED &&
          withinLastDay
        )
      }

      // Get all orders that match the matcher function and sort them by date in descending order
      // so that the most recent orders are at the top and will be synced first.
      return getMatchingRecords(Object.keys(orders), orders, matcher, limit).sort((a, b) => {
        const dateA = dateForOrder(orders[a])
        const dateB = dateForOrder(orders[b])

        return dateA - dateB
      })
    },
    [dateForOrder, orderInProgress?.uuid, orders],
  )

  const getOrderIdsToStateSync = useCallback(
    (includeSubmitted = true) => {
      // Append orders with an unknown order state, like 'submitted', even if they are not in the
      // offlineCardIds
      const currentMoment = moment()

      const matcher = (order) => {
        const orderDate = dateForOrder(order)
        let withinSevenDays = false
        if (orderDate) {
          const daysDifference = Math.abs(currentMoment.diff(orderDate, 'days'))
          withinSevenDays = daysDifference <= 7
        }

        // If the order is submitted OR in the list of offlineCardIds, we want to sync it as long as it is within 7 days.
        return (
          ((includeSubmitted && order?.state === STADIUM_ORDER_STATES.SUBMITTED) ||
            orderIds.includes(order?.id?.toString()) ||
            orderIds.includes(order?.uuid?.toString())) &&
          withinSevenDays
        )
      }

      // Get all orders that match the matcher function and sort them by date in descending order
      // so that the most recent orders are at the top and will be synced first.
      const ordersToSync = getMatchingRecords(Object.keys(orders), orders, matcher).sort((a, b) => {
        const dateA = dateForOrder(orders[a])
        const dateB = dateForOrder(orders[b])

        return dateA - dateB
      })

      return [...ordersToSync]
    },
    [dateForOrder, orderIds, orders],
  )

  const getOfflineLocalIds = useCallback(
    (limit) => {
      const matcher = (order) => (!order?.wasCreatedInStadium && !order?.isSyncing) || order?.needsSync
      const result = getMatchingRecords(Object.keys(orders), orders, matcher, limit)

      return result
    },
    [orders],
  )

  const getUnsyncedOrderCounts = useCallback(() => {
    const unsyncedLocalOrderCount = getOfflineLocalIds().length ?? 0
    const offlineCardCount = orderIds.length ?? 0
    if (isPaxDevice) {
      return [activeOfflineCardCount, unsyncedLocalOrderCount]
    }

    return [offlineCardCount, unsyncedLocalOrderCount]
  }, [activeOfflineCardCount, getOfflineLocalIds, isPaxDevice, orderIds.length])

  return {
    getSyncableOfflineLocalIds,
    getOrderIdsToStateSync,
    getUnsyncedOrderCounts,
  }
}

export default useOrderCounters
